import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isEqual } from 'lodash';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import useGetUserFeedbackQuery from '../queries/useGetUserFeedbackQuery';

const UserFeedbackFiltersContext = createContext();

export const useUserFeedbackFiltersContext = () => {
  const context = useContext(UserFeedbackFiltersContext);
  if (!context) {
    throw new Error(
      'useUserFeedbackFiltersContext must be used within a UserFeedbackFiltersProvider'
    );
  }
  return context;
};

// Default date range: Last 3 months
const getDefaultDateRange = () => {
  const start = moment()
    .subtract(3, 'months')
    .startOf('day')
    .format('YYYY-MM-DD');
  const end = moment().endOf('day').format('YYYY-MM-DD');
  return {
    start,
    end,
  };
};

const DEFAULT_FILTERS = {
  searchTerm: '',
  markedAsNotHelpful: false,
  tags: [],
  includeProductionHubs: true,
  includeSuperadmin: false,
  raisedTicket: undefined,
  flaggedInternally: undefined,
  dateRange: getDefaultDateRange(),
};

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 10,
};

// Helper functions to separate filters from pagination
const extractFilters = filters => {
  const { page, limit, ...filterValues } = filters;
  return filterValues;
};

export const UserFeedbackFiltersProvider = ({ children }) => {
  const [userFeedbackFilters, setUserFeedbackFilters] = useState({
    ...DEFAULT_FILTERS,
    ...DEFAULT_PAGINATION,
  });
  const [pendingFilters, setPendingFilters] = useState({
    ...DEFAULT_FILTERS,
    ...DEFAULT_PAGINATION,
  });
  const history = useHistory();

  const { refetch } = useGetUserFeedbackQuery(userFeedbackFilters);

  const updateFilters = useCallback((key, newValue) => {
    setPendingFilters(prev => {
      const updated = { ...prev, [key]: newValue };

      // Reset page to 1 when any filter other than page changes
      if (key !== 'page') {
        updated.page = 1;
      }

      return updated;
    });
  }, []);

  // Separate function for page changes that apply immediately
  const updatePage = useCallback(newPage => {
    setUserFeedbackFilters(prev => ({ ...prev, page: newPage }));
    setPendingFilters(prev => ({ ...prev, page: newPage }));
  }, []);

  const updateLimit = useCallback(newLimit => {
    setUserFeedbackFilters(prev => ({ ...prev, limit: newLimit }));
    setPendingFilters(prev => ({ ...prev, limit: newLimit }));
  }, []);

  const resetFilters = useCallback(() => {
    const currentFilters = extractFilters(pendingFilters);
    if (isEqual(currentFilters, DEFAULT_FILTERS)) {
      return;
    }
    setUserFeedbackFilters({ ...DEFAULT_FILTERS, ...DEFAULT_PAGINATION });
    setPendingFilters({ ...DEFAULT_FILTERS, ...DEFAULT_PAGINATION });
    // history.push('/support-widget/user-feedback');
  }, [pendingFilters, history]);

  const applyFilters = useCallback(() => {
    setUserFeedbackFilters(pendingFilters);
    // history.push('/support-widget/user-feedback');
  }, [pendingFilters, history]);

  const isDirty = useMemo(() => {
    const pendingFilterValues = extractFilters(pendingFilters);
    const appliedFilterValues = extractFilters(userFeedbackFilters);

    return !isEqual(appliedFilterValues, pendingFilterValues);
  }, [userFeedbackFilters, pendingFilters]);

  const enableClearAll = useMemo(() => {
    const pendingFilterValues = extractFilters(pendingFilters);
    return !isEqual(pendingFilterValues, DEFAULT_FILTERS);
  }, [pendingFilters]);

  // Only refetch when userFeedbackFilters (applied filters) change
  useEffect(() => {
    const appliedFilterValues = extractFilters(userFeedbackFilters);
    if (isEqual(appliedFilterValues, DEFAULT_FILTERS)) {
      refetch();
    }
  }, [userFeedbackFilters, refetch]);

  const value = {
    userFeedbackFilters,
    pendingFilters,
    isDirty,
    enableClearAll,
    setUserFeedbackFilters: updateFilters,
    updatePage,
    updateLimit,
    resetFilters,
    applyFilters,
  };

  return (
    <UserFeedbackFiltersContext.Provider value={value}>
      {children}
    </UserFeedbackFiltersContext.Provider>
  );
};
